import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Container, makeStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { cloneDeep, isEmpty, isEqual, uniq } from 'lodash';
import { useParams } from 'react-router-dom';
import { FormPassCodeTransaction, Page, RemarksModal } from 'src/components';
import { allowedPaymentOnTermsField, localize } from 'src/constants';
import { CategoriesEnum } from 'src/enums';
import { useFormPasscodeDialog, useIsMount, useSnackBar } from 'src/hooks';
import { slices, useAppDispatch } from 'src/redux';
import {
  AddReplaceTransactionRequest,
  ChangeSrpPayload,
  CreateTransactionPaymentRequest,
  CreateTransactionRequest,
  CustomInputEvent,
  DeleteTransactionPaymentRequest,
  FixMeLater,
  GetTransactionDetailResponse,
  GetTransactionPaymentsResponse,
  GetTransactionsViaTransactionNoResponse,
  Rma,
  Transaction,
  TransactionFieldOptions,
  TransactionPayment,
  UpdateTransactionPaymentRequest,
  UpdateTransactionRemarksRequest
} from 'src/types';
import { formatCurrency } from 'src/utils';
import { AddItemTransactionModal } from '../components';
import {
  AddTransactionPaymentModal,
  FailedChangeSerialAlertInfo,
  TransactionDetailsAlertInfo,
  TransactionDetailsHeader,
  TransactionInfo,
  TransactionPaymentsCard,
  TransactionsCard,
  TransactionsRmaCard
} from './components';
import { usePermissions } from 'src/hooks';
import {
  isPaymentBankTransfer,
  onTapTapCustomerPaymentAddedViaInternal,
  onTapTapCustomerPaymentDeleteViaInternal
} from 'src/redux/slices/taptap-customer';
import useUserInfo from 'src/hooks/user/use-user-info';
import usePrompt from 'src/utils/navigation-prompt';
import { useTransaction } from 'src/hooks/transaction/use-transaction';

const { actions: taptapActions } = slices.taptapCustomer;
const { actions: transactionActions } = slices.transaction;
const { actions: transactionPaymentActions } = slices.transactionPayments;
const { actions: rmaActions } = slices.rma;

interface Accumulator {
  counts: { [key: string]: number };
  maxCount: number;
  mostCommon: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    PointerEvent: 'none'
  },
  customerInfoBox: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },

  printButton: {
    marginBottom: 14
  },
  extraInfo: {
    margin: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  deleteBtn: {
    marginTop: 20
  },
  cardContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const TransactionInfoView = () => {
  const isPaymentsLoadingWithRef = useRef(false);
  const isFirstMount = useIsMount();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();
  const { transaction_no } = useParams();
  const { canAddTransactionPayment } = usePermissions();
  const {
    hideFormPasscodeDialog,
    validatePasscodeSuccess,
    validatePasscodeMessage,
    validatePasscodeError
  } = useFormPasscodeDialog();
  const { userDetails } = useUserInfo();
  const {
    transactionDetailsResponse,
    getTransactionDetails,
    onTransactionDetailsUpdate,
    transactionInfo,
    failedChangeSerial,
    isLoading,
    setIsLoading,
    setFailedChangeSerial,
    getTransactionInfo
  } = useTransaction();

  const orderNoOverrideOnURL = useMemo(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const orderNo = urlParams.get('on');
    return orderNo ? orderNo : undefined;
  }, []);

  const [transactionPaymentResponse, setTransactionPaymentResponse] = useState<
    GetTransactionPaymentsResponse
  >();

  const [rmas, setRmas] = useState<Rma[]>();
  const [itemToVoid, setItemToVoid] = useState<Transaction | undefined>();
  const [isVisibleItemToVoidDialog, setIsVisibleItemToVoidDialog] = useState(
    false
  );
  const [itemToAddOrReplace, setItemToAddOrReplace] = useState<
    AddReplaceTransactionRequest | undefined
  >();
  const [addPaymentModalVisible, setAddPaymentModalVisible] = useState<boolean>(
    false
  );

  const [isPaymentDeleteLoading, setIsPaymentDeleteLoading] = useState<boolean>(
    false
  );
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [paymentToBeDeleted, setPaymentToBeDeleted] = useState<
    TransactionPayment
  >();

  const [isDeleteTransaction, setCallDeleteWholeTransaction] = useState<
    boolean
  >();

  useEffect(() => {
    if (validatePasscodeSuccess) {
      hideFormPasscodeDialog();
      setIsVisibleItemToVoidDialog(true);
      snackBar.show({ severity: 'success', message: validatePasscodeMessage });
    }

    if (!validatePasscodeSuccess && validatePasscodeError) {
      snackBar.show({ severity: 'error', message: validatePasscodeError });
    }
  }, [
    snackBar,
    hideFormPasscodeDialog,
    validatePasscodeSuccess,
    validatePasscodeMessage,
    validatePasscodeError
  ]);

  const getTransactionPayments = useCallback(async () => {
    const response = unwrapResult(
      await dispatch(
        transactionPaymentActions.getTransactionPaymentsThunk(transaction_no)
      )
    );
    if (response?.success && !isEmpty(response?.originalData)) {
      setTransactionPaymentResponse(response?.originalData);
    }
  }, [dispatch, transaction_no]);

  const createTransactionPayment = async (
    data: CreateTransactionPaymentRequest
  ) => {
    if (!transaction_no || !canAddTransactionPayment) {
      return;
    }
    if (addPaymentModalVisible) {
      setAddPaymentModalVisible(false);
    }
    setIsPaymentLoading(true);
    isPaymentsLoadingWithRef.current = true;
    const response = unwrapResult(
      await dispatch(
        transactionPaymentActions.createTransactionPaymentThunk(data)
      )
    );
    if (response?.success && !isEmpty(response?.originalData)) {
      onTapTapCustomerPaymentAddedViaInternal(data).then(async () => {
        // send sms for confirmation for bank transfers
        if (isPaymentBankTransfer(data?.payment_type || '')) {
          const smsRes = unwrapResult(
            await dispatch(
              taptapActions.taptapSendSmsNotifThunk({
                transaction_no: data?.transaction_no ?? '',
                payment_type: data?.payment_type ?? '',
                amount: data?.amount ?? 0,
                action: 'for_confirmation'
              })
            )
          );
          const { success, errors, error, message } = smsRes.originalData;

          if (!success) {
            console.error(
              errors ??
                error ??
                message ??
                'Failed to to send sms notif for this payment'
            );
          }
        }

        getTransactionPayments().then(() => {
          snackBar.show({
            severity: 'success',
            message: `${response.message} Balance left: ${formatCurrency(
              response?.originalData?.balance
            )}`
          });
        });
      });
    } else {
      snackBar.show({
        severity: 'error',
        message: `${response?.errors?.payment_type?.[0] ??
          (response?.message || 'Error')}`
      });
    }
    setIsPaymentLoading(false);
    isPaymentsLoadingWithRef.current = false;
  };

  const onDeleteTransactionPayment = async (
    data: DeleteTransactionPaymentRequest
  ) => {
    if (!transaction_no) {
      return;
    }
    if (paymentToBeDeleted) {
      setPaymentToBeDeleted(undefined);
    }
    setIsPaymentDeleteLoading(true);
    isPaymentsLoadingWithRef.current = true;
    const response = unwrapResult(
      await dispatch(
        transactionPaymentActions.deleteTransactionPaymentThunk(data)
      )
    );
    if (response?.success && !isEmpty(response?.originalData)) {
      snackBar.show({
        severity: 'success',
        message: `${response.message} Balance left: ${formatCurrency(
          response?.originalData?.balance
        )}`
      });

      const dataPaymentToBeDeleted = transactionPaymentResponse?.data?.find(
        (x) => x.id === data.id
      );

      console.log("userDetails?.id", userDetails);

      if (dataPaymentToBeDeleted) {
        onTapTapCustomerPaymentDeleteViaInternal({
          userDetailsId: userDetails?.id,
          transactionNo: transaction_no,
          paymentToBeDeleted: dataPaymentToBeDeleted
        });
      }

      getTransactionPayments();
    } else {
      snackBar.show({
        severity: 'error',
        message: `${response.message || 'Error'}`
      });
    }
    setIsPaymentDeleteLoading(false);
    isPaymentsLoadingWithRef.current = false;
  };

  const getRmas = async () => {
    const response = unwrapResult(
      await dispatch(rmaActions.getRmasViaTransactionNoThunk(transaction_no))
    );
    if (response?.success && !isEmpty(response?.originalData)) {
      setRmas(response?.originalData?.rmas);
    }
  };

  // NOTE: In case we want old printing again
  // const onPrintPress = () => {
  //   navigate(`/app/transaction/print`, {
  //     state: transactionInfo
  //   });
  // };

  const transactionItems = useMemo(() => transactionInfo?.items ?? [], [
    transactionInfo
  ]);

  const mostUsedBranch = useMemo(() => {
    const accumulator = transactionItems.reduce<Accumulator>(
      (acc, item) => {
        // Count the occurrences of each branch_name
        const { branch_name: branch, void_reason: isVoided } = item;

        if (typeof branch === 'string') {
          if (!isVoided) {
            acc.counts[branch] = (acc.counts[branch] || 0) + 1;
          }

          if (acc.counts[branch] > acc.maxCount) {
            acc.maxCount = acc.counts[branch];
            acc.mostCommon = branch;
          }
        }

        return acc;
      },
      { counts: {}, maxCount: 0, mostCommon: '' }
    );
    return accumulator.mostCommon;
  }, [transactionItems]);

  const isReceiptSRPByDefault = useMemo(() => {
    const mappedTerms = transactionPaymentResponse?.data?.map((x) => {
      if (allowedPaymentOnTermsField?.includes(x.payment_type || '')) {
        return x.payment_type || '';
      }
      return '';
    });
    const uniqTerms: string[] = uniq(mappedTerms);
    const termsWithSRP = ['PAYMONGO'];
    const hasSRPTerm = uniqTerms?.some((r) => termsWithSRP?.indexOf(r) >= 0);

    if (hasSRPTerm && transactionDetailsResponse?.data?.ecomm_order_ref_no) {
      return true;
    }
    return false;
  }, [transactionDetailsResponse, transactionPaymentResponse]);

  const onPrintPress = () => {
    const mappedTerms = transactionPaymentResponse?.data?.map((x) => {
      if (x?.deleted_by) {
        return '';
      }
      return x?.payment_type || '';
    });
    const uniqTerms: string[] = uniq(mappedTerms)?.filter((x) => x !== '');

    // Transform data first.

    const initialPaymentsResponse = {
      success: false,
      total_transaction_amount: transactionInfo?.total_price,
      total_paid_amount: 0,
      total_balance: transactionInfo?.total_price,
      data: []
    };

    let transactionInfoWithOrderNo = {
      ...transactionInfo,
      order_no:
        orderNoOverrideOnURL ||
        transactionDetailsResponse?.data?.order_no ||
        '',
      isSRPByDefault: isReceiptSRPByDefault,
      store_address: transactionDetailsResponse?.data?.store_address,
      ec_order_id: transactionDetailsResponse?.data?.ec_order_id,
      payment_mode: uniqTerms?.join(', '),
      paymentsResponse:
        !transactionPaymentResponse || !transactionPaymentResponse?.success
          ? initialPaymentsResponse
          : transactionPaymentResponse
    };
    transactionInfoWithOrderNo.items = transactionInfoWithOrderNo?.items?.filter(
      (x) => x.isDeleted === 0
    );

    const receiptItemsOrder = [
      CategoriesEnum.GPU,
      CategoriesEnum.CPU,
      CategoriesEnum.Motherboard,
      CategoriesEnum.RAM,
      CategoriesEnum.SSD,
      CategoriesEnum.HDD,
      CategoriesEnum.PSU,
      CategoriesEnum.Casing,
      CategoriesEnum.Monitor
    ];

    transactionInfoWithOrderNo?.items?.sort(function(a, b) {
      if (receiptItemsOrder?.indexOf(a?.category_id || 0) <= -1) {
        return 1;
      }
      if (receiptItemsOrder?.indexOf(b?.category_id || 0) <= -1) {
        return -1;
      }
      return (
        receiptItemsOrder.indexOf(a?.category_id || 0) -
        receiptItemsOrder.indexOf(b?.category_id || 0)
      );
    });

    sessionStorage.setItem(
      'transactionData',
      JSON.stringify({
        ...transactionInfoWithOrderNo,
        id: transactionDetailsResponse?.data?.id,
        business_name: transactionDetailsResponse?.data?.business_name,
        business_address: transactionDetailsResponse?.data?.business_address,
        tin: transactionDetailsResponse?.data?.tin,
        terms: transactionDetailsResponse?.data?.terms
      })
    );

    if (orderNoOverrideOnURL) {
      window.open(`/app/transaction/print?on=${orderNoOverrideOnURL}`);
      return;
    }

    // Open the new tab using window.open
    window.open('/app/transaction/print', '_blank');
  };

  useEffect(() => {
    if (
      orderNoOverrideOnURL &&
      transactionDetailsResponse &&
      transactionInfo &&
      transactionPaymentResponse
    ) {
      onPrintPress();
    }
  }, [
    orderNoOverrideOnURL,
    transactionDetailsResponse,
    transactionInfo,
    transactionPaymentResponse
  ]);

  const onCloseAddReplaceModal = () => {
    setItemToAddOrReplace(undefined);
  };

  const onDeletePress = () => {
    setIsVisibleItemToVoidDialog(true);
    setCallDeleteWholeTransaction(true);
  };

  const onVoidItem = (item: Transaction) => {
    setItemToVoid(item);
    setIsVisibleItemToVoidDialog(true);
  };

  const onReplaceItem = async (newItem: CreateTransactionRequest) => {
    setIsLoading(true);
    const response = unwrapResult(
      await dispatch(
        transactionActions.replaceItemToTransactionNoThunk(newItem)
      ).finally(() => setIsLoading(false))
    );

    if (response?.success && response?.originalData) {
      getTransactionInfo();
      snackBar.show({
        message: response?.message || localize.ERR_API_UNKNOWN,
        severity: 'success'
      });
      return;
    }
    snackBar.show({
      message: response?.message || localize.ERR_API_UNKNOWN,
      severity: 'error'
    });
  };

  const onAddItem = async (newItem: CreateTransactionRequest) => {
    const newItemEcOrder = {
      ...newItem,
      ec_order_id: transactionDetailsResponse?.data?.ec_order_id
    };
    setIsLoading(true);
    const response = unwrapResult(
      await dispatch(
        transactionActions.addItemToTransactionNoThunk(newItemEcOrder)
      ).finally(() => setIsLoading(false))
    );
    if (response?.success && response?.originalData) {
      getTransactionInfo('added_item');
      getTransactionPayments();
      snackBar.show({
        message: response?.message || localize.ERR_API_UNKNOWN,
        severity: 'success'
      });
      return;
    }
    setIsLoading(false);
    snackBar.show({
      message: response?.message || localize.ERR_API_UNKNOWN,
      severity: 'error'
    });
  };

  const onAddOrReplace = (data: AddReplaceTransactionRequest) => {
    onCloseAddReplaceModal();
    if (data?.mode === 'add') {
      onAddItem(data);
    }
    if (data?.mode === 'replace') {
      onReplaceItem(data);
    }
  };

  const onAddItemPress = async () => {
    setItemToAddOrReplace({ mode: 'add', transaction_no });
  };

  const onReplaceItemPress = async (toBeReplacedItem: Transaction) => {
    setItemToAddOrReplace({
      mode: 'replace',
      transaction_no,
      toBeReplacedItem
    });
  };

  const onTransactionRemarksChange = async (
    newDataRemarks: UpdateTransactionRemarksRequest
  ) => {
    const response = unwrapResult(
      await dispatch(
        transactionActions.updateTransactionRemarksThunk(newDataRemarks)
      )
    );
    if (response?.success && !isEmpty(response?.originalData)) {
      snackBar.show({
        severity: 'success',
        message: 'transaction remarks update success'
      });
      dispatch(
        transactionActions.getTransactionViaTransactionNo(transaction_no)
      );
    } else {
      snackBar.show({
        severity: 'error',
        message: 'transaction remarks update failed'
      });
    }
  };

  const onTransactionDetailsCreateNew = async (
    newDate: GetTransactionDetailResponse
  ) => {
    const response = unwrapResult(
      await dispatch(
        transactionActions.createTransactionDetailThunk({
          ...newDate.data,
          transaction_no: transactionInfo?.transaction_no
        })
      )
    );
    if (response?.success && !isEmpty(response?.originalData)) {
      snackBar.show({
        severity: 'success',
        message: 'transaction details update success'
      });
      getTransactionDetails();
      return;
    }
    if (!response?.success && response?.errors) {
      snackBar.show({
        severity: 'error',
        message: `${JSON.stringify(response?.errors || '')}`
      });
      return;
    }
    snackBar.show({
      severity: 'error',
      message: 'transaction details update failed'
    });
  };

  const onTransactionInfoUpdate = async (
    newTransactionInfo: GetTransactionsViaTransactionNoResponse,
    newTransactionDetails: GetTransactionDetailResponse,
    newTransactionOptions?: TransactionFieldOptions
  ) => {
    const newTransactionPayload = {
      ...newTransactionDetails,
      data: {
        ...newTransactionDetails?.data,
        purchase_category:
          newTransactionOptions?.purchase_category ||
          transactionDetailsResponse?.data?.purchase_category,
        type_of_shipping:
          newTransactionOptions?.type_of_shipping ||
          transactionDetailsResponse?.data?.type_of_shipping,
        build_type:
          newTransactionOptions?.build_type ||
          transactionDetailsResponse?.data?.build_type,
        type_of_sales_transaction:
          newTransactionOptions?.type_of_sales_transaction ||
          transactionDetailsResponse?.data?.type_of_sales_transaction,
        voucher: transactionDetailsResponse?.data?.voucher || {}
      }
    };
    // remarks updated.
    if (
      transactionInfo?.transaction_remarks !==
      newTransactionInfo?.transaction_remarks
    ) {
      const newDataRemarks = {
        transaction_no: transactionInfo?.transaction_no,
        remarks: newTransactionInfo?.transaction_remarks
      };
      onTransactionRemarksChange(newDataRemarks);
    }

    // Meaning New, Should Create
    if (!newTransactionDetails?.data?.id) {
      onTransactionDetailsCreateNew(newTransactionDetails);
      return;
    }
    // else update only if changed / update
    if (!isEqual(transactionDetailsResponse, newTransactionPayload)) {
      onTransactionDetailsUpdate(newTransactionPayload);
      return;
    }
  };

  const onChangeARFieldText = useCallback(
    (event: CustomInputEvent, index: number) => {
      const { value } = event.target;
      const clonedPaymentResponse = cloneDeep(transactionPaymentResponse);
      if (clonedPaymentResponse?.data) {
        clonedPaymentResponse.data[index].acknowledgement_receipt_no = value;
      }
      setTransactionPaymentResponse(clonedPaymentResponse);
    },
    [transactionPaymentResponse]
  );

  const onChangeTFFieldText = useCallback(
    (event: CustomInputEvent, index: number) => {
      const { value } = event.target;
      const clonedPaymentResponse = cloneDeep(transactionPaymentResponse);

      if (clonedPaymentResponse?.data) {
        clonedPaymentResponse.data[index].transfer_fee =
          value === '' ? '' : Number(value);
      }
      setTransactionPaymentResponse(clonedPaymentResponse);
    },
    [transactionPaymentResponse]
  );

  const onSaveARField = useCallback(
    (
      e: FixMeLater,
      item: TransactionPayment,
      field: 'transfer_fee' | 'acknowledgement_receipt_no'
    ) => {
      e.stopPropagation();
      const newPaymentData: UpdateTransactionPaymentRequest = {
        id: item?.id,
        [field]:
          field === 'transfer_fee'
            ? item.transfer_fee === ''
              ? undefined
              : item.transfer_fee
            : item.acknowledgement_receipt_no
      };
      setIsPaymentLoading(true);
      dispatch(
        transactionPaymentActions.updateTransactionPaymentThunk(newPaymentData)
      ).finally(() => {
        setIsPaymentLoading(false);
        getTransactionPayments();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onHandleChangeSrp = useCallback(
    async (params?: ChangeSrpPayload) => {
      try {
        const response = unwrapResult(
          await dispatch(transactionActions?.changeTransactionSrpThunk(params))
        );

        if (response?.success) {
          snackBar.show({ severity: 'success', message: response?.message });
          getTransactionDetails();
          getTransactionInfo('srp_percent_change');
          getTransactionPayments();
        } else {
          snackBar.show({
            severity: 'error',
            message: 'Something went wrong please try again later'
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      dispatch,
      getTransactionDetails,
      getTransactionInfo,
      getTransactionPayments,
      snackBar
    ]
  );

  useEffect(() => {
    if (isFirstMount) {
      getTransactionInfo();
      getTransactionDetails();
      getRmas();
      getTransactionPayments();
    }
    if (validatePasscodeSuccess) {
      getTransactionInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstMount, validatePasscodeSuccess]);

  usePrompt(
    `Payment is still loading. Are you sure you want to leave this page?`,
    isPaymentLoading || isPaymentsLoadingWithRef.current
  );

  const onHandleFailedChangeSerial = useCallback(
    (failed: boolean) => {
      if (failed) {
        setFailedChangeSerial(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Page title="Transaction Details" className={clsx(classes.root)}>
      <Container maxWidth={false}>
        <TransactionDetailsHeader
          isLoading={isLoading || isPaymentLoading}
          onPrintPress={onPrintPress}
        />
        <TransactionInfo
          isLoading={isLoading}
          onUpdate={onTransactionInfoUpdate}
          transactionInfo={transactionInfo}
          transactionDetails={transactionDetailsResponse}
          transactionPayments={transactionPaymentResponse?.data || []}
        />

        <TransactionDetailsAlertInfo />

        {failedChangeSerial && (
          <>
            <div style={{ margin: '10px 0' }}>
              <FailedChangeSerialAlertInfo />
            </div>
          </>
        )}

        <TransactionsCard
          isLoading={isLoading}
          transactionInfo={transactionInfo}
          transactionDetails={transactionDetailsResponse?.data}
          onVoidItem={onVoidItem}
          onAddItemPress={onAddItemPress}
          onReplaceItemPress={onReplaceItemPress}
          onChangeSrp={(params?: ChangeSrpPayload) => onHandleChangeSrp(params)}
          // setTransactionToRma={setTransactionToRma}
          getDeleteTransactionInfo={() => setCallDeleteWholeTransaction(false)}
          getTransaction={() => getTransactionInfo('voided_item')}
          getTransactionPayments={getTransactionPayments}
          onHandleFailedChangeSerial={onHandleFailedChangeSerial}
        />

        {isLoading ? null : (
          <TransactionPaymentsCard
            isLoading={isPaymentLoading}
            data={transactionPaymentResponse}
            transactionNo={transactionInfo?.transaction_no}
            onDeletePayment={(data) => setPaymentToBeDeleted(data)}
            onAddPaymentPress={() => setAddPaymentModalVisible(true)}
            onSaveARField={onSaveARField}
            onChangeTFField={onChangeTFFieldText}
            onChangeARFieldText={onChangeARFieldText}
          />
        )}

        {rmas && rmas?.length > 0 ? <TransactionsRmaCard rmas={rmas} /> : null}

        <Button
          className={classes.deleteBtn}
          startIcon={<DeleteIcon />}
          onClick={onDeletePress}
          color="secondary"
          variant="contained"
        >
          Delete Transaction
        </Button>
      </Container>

      <AddItemTransactionModal
        data={itemToAddOrReplace}
        mostUsedBranch={mostUsedBranch}
        onAddOrReplace={onAddOrReplace}
        visible={itemToAddOrReplace ? true : false}
        onHandleClose={onCloseAddReplaceModal}
      />

      {canAddTransactionPayment && (
        <AddTransactionPaymentModal
          visible={addPaymentModalVisible}
          onHandleClose={() => setAddPaymentModalVisible(false)}
          transactionNo={transaction_no}
          onApplyPress={createTransactionPayment}
          transactionPayments={transactionPaymentResponse}
        />
      )}

      <RemarksModal
        loading={isPaymentDeleteLoading}
        data={paymentToBeDeleted}
        fieldName="Remarks / Reason for deletion"
        visible={paymentToBeDeleted ? true : false}
        onDeletePress={onDeleteTransactionPayment}
        onHandleClose={() => setPaymentToBeDeleted(undefined)}
        title={`Delete a payment (${formatCurrency(
          paymentToBeDeleted?.amount
        )} - ${paymentToBeDeleted?.payment_type})`}
      />

      <FormPassCodeTransaction
        isVisible={isVisibleItemToVoidDialog}
        onClose={() => setIsVisibleItemToVoidDialog(false)}
        itemToVoid={itemToVoid}
        transactionInfo={transactionInfo}
        transaction_no={transaction_no}
        getTransaction={() => getTransactionInfo('voided_item')}
        getTransactionPayments={getTransactionPayments}
        setItemVoid={() => setItemToVoid(undefined)}
        isDeleteTransaction={isDeleteTransaction}
      />
    </Page>
  );
};

export default TransactionInfoView;
